/**
 * Email Template Codes — mirrors the `code` column in `email_templates` table.
 * Use these constants instead of raw strings to avoid typos.
 */

// Procurement Module
export const TEMPLATE_PR_SUBMITTED = 'PR_SUBMITTED' as const;
export const TEMPLATE_PR_APPROVED = 'PR_APPROVED' as const;
export const TEMPLATE_PR_REJECTED = 'PR_REJECTED' as const;
export const TEMPLATE_RFQ_SENT = 'RFQ_SENT' as const;
export const TEMPLATE_RFQ_RESPONSE_RECEIVED = 'RFQ_RESPONSE_RECEIVED' as const;
export const TEMPLATE_RFQ_DEADLINE_APPROACHING = 'RFQ_DEADLINE_APPROACHING' as const;
export const TEMPLATE_QUOTATION_RECEIVED = 'QUOTATION_RECEIVED' as const;
export const TEMPLATE_QUOTATION_APPROVED = 'QUOTATION_APPROVED' as const;
export const TEMPLATE_PO_SENT = 'PO_SENT' as const;
export const TEMPLATE_PO_OVERDUE = 'PO_OVERDUE' as const;
export const TEMPLATE_PO_CLOSED = 'PO_CLOSED' as const;
export const TEMPLATE_PRICE_LIST_EXPIRING = 'PRICE_LIST_EXPIRING' as const;

// Inventory & Warehouse Module
export const TEMPLATE_GRN_RECEIVED = 'GRN_RECEIVED' as const;
export const TEMPLATE_GRN_QC_HOLD = 'GRN_QC_HOLD' as const;
export const TEMPLATE_STOCK_BELOW_REORDER = 'STOCK_BELOW_REORDER' as const;
export const TEMPLATE_NEGATIVE_STOCK_ALERT = 'NEGATIVE_STOCK_ALERT' as const;
export const TEMPLATE_SRN_CREATED = 'SRN_CREATED' as const;
export const TEMPLATE_SRN_DISPATCHED = 'SRN_DISPATCHED' as const;
export const TEMPLATE_INVENTORY_ADJUSTMENT_POSTED = 'INVENTORY_ADJUSTMENT_POSTED' as const;
export const TEMPLATE_STOCK_CORRECTION_REQUESTED = 'STOCK_CORRECTION_REQUESTED' as const;
export const TEMPLATE_MATERIAL_ISSUE_REQUESTED = 'MATERIAL_ISSUE_REQUESTED' as const;
export const TEMPLATE_MATERIAL_ISSUE_APPROVED = 'MATERIAL_ISSUE_APPROVED' as const;

// Manufacturing / Production Module
export const TEMPLATE_WO_CREATED = 'WO_CREATED' as const;
export const TEMPLATE_WO_STARTED = 'WO_STARTED' as const;
export const TEMPLATE_WO_COMPLETED = 'WO_COMPLETED' as const;
export const TEMPLATE_WO_CANCELLED = 'WO_CANCELLED' as const;
export const TEMPLATE_WO_OVERDUE = 'WO_OVERDUE' as const;
export const TEMPLATE_JOB_CARD_QC_FAILED = 'JOB_CARD_QC_FAILED' as const;
export const TEMPLATE_JOB_CARD_QC_HOLD = 'JOB_CARD_QC_HOLD' as const;
export const TEMPLATE_DEFECT_LOGGED = 'DEFECT_LOGGED' as const;
export const TEMPLATE_FG_RECEIPT_CREATED = 'FG_RECEIPT_CREATED' as const;
export const TEMPLATE_DC_DISPATCHED = 'DC_DISPATCHED' as const;
export const TEMPLATE_DC_DELIVERED = 'DC_DELIVERED' as const;
export const TEMPLATE_SUBCON_DISPATCHED = 'SUBCON_DISPATCHED' as const;

// Subcontracting Module
export const TEMPLATE_SUBCON_RECEIVED = 'SUBCON_RECEIVED' as const;
export const TEMPLATE_SUBCON_OVERDUE = 'SUBCON_OVERDUE' as const;
export const TEMPLATE_SUBCON_CLOSED = 'SUBCON_CLOSED' as const;
export const TEMPLATE_SUBCON_REJECT_ALERT = 'SUBCON_REJECT_ALERT' as const;

// MRP / Planning Module
export const TEMPLATE_MRP_RUN_COMPLETED = 'MRP_RUN_COMPLETED' as const;
export const TEMPLATE_MRP_CRITICAL_SHORTAGE = 'MRP_CRITICAL_SHORTAGE' as const;
export const TEMPLATE_MRP_DAILY_DIGEST = 'MRP_DAILY_DIGEST' as const;
export const TEMPLATE_MRP_STOCK_OUT_PROJECTED = 'MRP_STOCK_OUT_PROJECTED' as const;

// Projects & Tasks Module
export const TEMPLATE_TASK_ASSIGNED = 'TASK_ASSIGNED' as const;
export const TEMPLATE_TASK_OVERDUE = 'TASK_OVERDUE' as const;
export const TEMPLATE_PROJECT_STAGE_COMPLETED = 'PROJECT_STAGE_COMPLETED' as const;
export const TEMPLATE_PROJECT_MILESTONE = 'PROJECT_MILESTONE' as const;

// User & System Module
export const TEMPLATE_USER_CREATED = 'USER_CREATED' as const;
export const TEMPLATE_DAILY_SYSTEM_DIGEST = 'DAILY_SYSTEM_DIGEST' as const;

/**
 * Module names for the `module` column in email_notifications
 */
export const MODULE = {
  PROCUREMENT: 'procurement',
  INVENTORY: 'inventory',
  PRODUCTION: 'production',
  SUBCONTRACTING: 'subcontracting',
  MRP: 'mrp',
  PROJECTS: 'projects',
  SYSTEM: 'system',
} as const;

/**
 * Notification priority levels
 */
export const PRIORITY = {
  LOW: 'LOW',
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  URGENT: 'URGENT',
} as const;

export type TemplateCode =
  | typeof TEMPLATE_PR_SUBMITTED
  | typeof TEMPLATE_PR_APPROVED
  | typeof TEMPLATE_PR_REJECTED
  | typeof TEMPLATE_RFQ_SENT
  | typeof TEMPLATE_RFQ_RESPONSE_RECEIVED
  | typeof TEMPLATE_RFQ_DEADLINE_APPROACHING
  | typeof TEMPLATE_QUOTATION_RECEIVED
  | typeof TEMPLATE_QUOTATION_APPROVED
  | typeof TEMPLATE_PO_SENT
  | typeof TEMPLATE_PO_OVERDUE
  | typeof TEMPLATE_PO_CLOSED
  | typeof TEMPLATE_PRICE_LIST_EXPIRING
  | typeof TEMPLATE_GRN_RECEIVED
  | typeof TEMPLATE_GRN_QC_HOLD
  | typeof TEMPLATE_STOCK_BELOW_REORDER
  | typeof TEMPLATE_NEGATIVE_STOCK_ALERT
  | typeof TEMPLATE_SRN_CREATED
  | typeof TEMPLATE_SRN_DISPATCHED
  | typeof TEMPLATE_INVENTORY_ADJUSTMENT_POSTED
  | typeof TEMPLATE_STOCK_CORRECTION_REQUESTED
  | typeof TEMPLATE_MATERIAL_ISSUE_REQUESTED
  | typeof TEMPLATE_MATERIAL_ISSUE_APPROVED
  | typeof TEMPLATE_WO_CREATED
  | typeof TEMPLATE_WO_STARTED
  | typeof TEMPLATE_WO_COMPLETED
  | typeof TEMPLATE_WO_CANCELLED
  | typeof TEMPLATE_WO_OVERDUE
  | typeof TEMPLATE_JOB_CARD_QC_FAILED
  | typeof TEMPLATE_JOB_CARD_QC_HOLD
  | typeof TEMPLATE_DEFECT_LOGGED
  | typeof TEMPLATE_FG_RECEIPT_CREATED
  | typeof TEMPLATE_DC_DISPATCHED
  | typeof TEMPLATE_DC_DELIVERED
  | typeof TEMPLATE_SUBCON_DISPATCHED
  | typeof TEMPLATE_SUBCON_RECEIVED
  | typeof TEMPLATE_SUBCON_OVERDUE
  | typeof TEMPLATE_SUBCON_CLOSED
  | typeof TEMPLATE_SUBCON_REJECT_ALERT
  | typeof TEMPLATE_MRP_RUN_COMPLETED
  | typeof TEMPLATE_MRP_CRITICAL_SHORTAGE
  | typeof TEMPLATE_MRP_DAILY_DIGEST
  | typeof TEMPLATE_MRP_STOCK_OUT_PROJECTED
  | typeof TEMPLATE_TASK_ASSIGNED
  | typeof TEMPLATE_TASK_OVERDUE
  | typeof TEMPLATE_PROJECT_STAGE_COMPLETED
  | typeof TEMPLATE_PROJECT_MILESTONE
  | typeof TEMPLATE_USER_CREATED
  | typeof TEMPLATE_DAILY_SYSTEM_DIGEST;

export type ModuleName = (typeof MODULE)[keyof typeof MODULE];
export type PriorityLevel = (typeof PRIORITY)[keyof typeof PRIORITY];
